import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Settings,
  CreditCard,
  Plus,
  Trash2,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import * as api from "@/src/api/sdk.gen";
import type { OrgStats } from "../types";
import { getErrorMessage } from "../types";
import { ConfirmActionModal, type ConfirmAction } from "../components/modals";

export interface SettingsPageProps {
  slug: string;
  stats: OrgStats;
  billingInfo?: any;
  onStatsUpdated: (stats: OrgStats) => void;
  loadData: () => Promise<void>;
}

export const SettingsPage: React.FC<SettingsPageProps> = ({
  slug,
  stats,
  billingInfo,
  onStatsUpdated,
  loadData,
}) => {
  const navigate = useNavigate();

  const [settingsDraft, setSettingsDraft] = useState({
    name: stats.organization_name || stats.name || "",
    slug: stats.slug || "",
    description: stats.description || "",
    seat_limit: stats.seat_limit ? String(stats.seat_limit) : "",
    join_link_enabled: stats.join_link_enabled ?? true,
    allowed_domains: [...(stats.allowed_domains || [])],
    newDomainInput: "",
    departments: [...(stats.departments || [])],
    newDeptInput: "",
  });

  useEffect(() => {
    if (stats) {
      setSettingsDraft({
        name: stats.organization_name || stats.name || "",
        slug: stats.slug || "",
        description: stats.description || "",
        seat_limit: stats.seat_limit ? String(stats.seat_limit) : "",
        join_link_enabled: stats.join_link_enabled ?? true,
        allowed_domains: [...(stats.allowed_domains || [])],
        newDomainInput: "",
        departments: [...(stats.departments || [])],
        newDeptInput: "",
      });
    }
  }, [stats]);

  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);

  // Seat update states
  const [newSeatLimit, setNewSeatLimit] = useState<number | "">("");
  const [updatingSeats, setUpdatingSeats] = useState(false);
  const [regeneratingToken, setRegeneratingToken] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const handleAddDomain = () => {
    const input = settingsDraft.newDomainInput.trim().toLowerCase().replace("@", "");
    if (!input) return;
    if (settingsDraft.allowed_domains.includes(input)) return;
    setSettingsDraft((prev) => ({
      ...prev,
      allowed_domains: [...prev.allowed_domains, input],
      newDomainInput: "",
    }));
  };

  const handleRemoveDomain = (domain: string) => {
    setSettingsDraft((prev) => ({
      ...prev,
      allowed_domains: prev.allowed_domains.filter((d) => d !== domain),
    }));
  };

  const handleAddDept = () => {
    const input = settingsDraft.newDeptInput.trim();
    if (!input) return;
    if (settingsDraft.departments.includes(input)) return;
    setSettingsDraft((prev) => ({
      ...prev,
      departments: [...prev.departments, input],
      newDeptInput: "",
    }));
  };

  const handleRemoveDept = (dept: string) => {
    setSettingsDraft((prev) => ({
      ...prev,
      departments: prev.departments.filter((d) => d !== dept),
    }));
  };

  const handleUpdateSeats = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSeatLimit) return;
    try {
      setUpdatingSeats(true);
      const { data, error } = await api.updateSeatAllocationApiV1B2bSlugBillingUpdateSeatsPost({
        path: { slug },
        body: { seat_limit: Number(newSeatLimit) },
      });
      if (data) {
        setSettingsMessage({ text: `Seat limit updated to ${(data as any).seat_limit} seats!`, type: "success" });
        await loadData();
      } else {
        setSettingsMessage({ text: getErrorMessage(error, "Failed to update seat limit."), type: "error" });
      }
    } catch (err) {
      setSettingsMessage({ text: "Failed to update seat limit.", type: "error" });
    } finally {
      setUpdatingSeats(false);
    }
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug) return;
    try {
      setSavingSettings(true);
      setSettingsMessage(null);

      const { data, error } = await api.updateOrgSettingsApiV1B2bSlugSettingsPatch({
        path: { slug },
        body: {
          name: settingsDraft.name,
          slug: settingsDraft.slug,
          description: settingsDraft.description,
          seat_limit: settingsDraft.seat_limit ? parseInt(settingsDraft.seat_limit, 10) : null,
          join_link_enabled: settingsDraft.join_link_enabled,
          allowed_domains: settingsDraft.allowed_domains,
          departments: settingsDraft.departments,
        },
      });

      if (!data) {
        setSettingsMessage({ text: getErrorMessage(error, "Failed to update settings"), type: "error" });
        return;
      }

      onStatsUpdated(data as unknown as OrgStats);
      setSettingsMessage({ text: "Organization workspace settings saved successfully!", type: "success" });

      if ((data as any).slug && (data as any).slug !== slug) {
        navigate(`/org/${(data as any).slug}/dashboard`, { replace: true });
      }
    } catch (err: any) {
      setSettingsMessage({ text: "Error saving workspace settings.", type: "error" });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleRegenerateLink = () => {
    if (!stats) return;
    setConfirmAction({
      title: "Regenerate organization link?",
      description: "The current private join link will stop working immediately. A new link will be created for future members.",
      confirmLabel: "Regenerate link",
      onConfirm: async () => {
        try {
          setRegeneratingToken(true);
          const { data, error } = await api.regenerateJoinLinkApiV1B2bSlugRegenerateLinkPost({
            path: { slug },
          });
          if (data) {
            await loadData();
            setSettingsMessage({ text: "Join link regenerated successfully!", type: "success" });
          } else {
            setSettingsMessage({ text: getErrorMessage(error, "Failed to regenerate join link."), type: "error" });
          }
        } catch {
          setSettingsMessage({ text: "Failed to regenerate join link.", type: "error" });
        } finally {
          setRegeneratingToken(false);
        }
      },
    });
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <ConfirmActionModal action={confirmAction} onClose={() => setConfirmAction(null)} />
      {/* Subscription & Seat Licensing Widget */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-xs">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-indigo-600" />
            Subscription &amp; Seat Licensing
          </h3>
          <span className="px-3 py-1 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-full text-xs font-bold uppercase font-mono">
            {billingInfo?.plan_tier || stats.plan_tier || "Standard"} Tier
          </span>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl">
            <span className="text-[10px] font-bold uppercase text-slate-500">Total Seats</span>
            <div className="text-xl font-black text-slate-900 mt-1">
              {billingInfo?.seat_limit || stats.seat_limit || 50}
            </div>
          </div>
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl">
            <span className="text-[10px] font-bold uppercase text-slate-500">Seats Used</span>
            <div className="text-xl font-black text-indigo-600 mt-1">
              {billingInfo?.seats_used || stats.total_members}
            </div>
          </div>
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl">
            <span className="text-[10px] font-bold uppercase text-slate-500">Available</span>
            <div className="text-xl font-black text-emerald-600 mt-1">
              {billingInfo?.available_seats ?? Math.max(0, (stats.seat_limit || 50) - stats.total_members)}
            </div>
          </div>
        </div>

        <form onSubmit={handleUpdateSeats} className="flex gap-2">
          <input
            type="number"
            min={1}
            placeholder="Set new seat allocation limit..."
            value={newSeatLimit}
            onChange={(e) => setNewSeatLimit(e.target.value ? Number(e.target.value) : "")}
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500 shadow-xs"
          />
          <button
            type="submit"
            disabled={updatingSeats || !newSeatLimit}
            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shrink-0 shadow-xs disabled:opacity-50 transition"
          >
            {updatingSeats ? "Updating..." : "Update Seat Quota"}
          </button>
        </form>
      </div>

      {/* Workspace & Security Settings Form */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-xs">
        <h3 className="text-base font-bold text-slate-900 mb-6 flex items-center gap-2">
          <Settings className="w-5 h-5 text-indigo-600" />
          Workspace &amp; Security Settings
        </h3>

        {settingsMessage && (
          <div
            className={`mb-6 p-4 rounded-2xl text-xs flex items-center gap-2.5 ${
              settingsMessage.type === "success"
                ? "bg-emerald-50 border border-emerald-200 text-emerald-800 font-medium"
                : "bg-rose-50 border border-rose-200 text-rose-800 font-medium"
            }`}
          >
            {settingsMessage.type === "success" ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            )}
            {settingsMessage.text}
          </div>
        )}

        <form onSubmit={handleSaveSettings} className="space-y-6">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
              Organization Name
            </label>
            <input
              type="text"
              value={settingsDraft.name}
              onChange={(e) => setSettingsDraft((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500 shadow-xs transition"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
              URL Slug (Join URL identifier)
            </label>
            <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 shadow-xs focus-within:bg-white focus-within:border-indigo-500">
              <span className="text-xs text-slate-400 mr-1 font-mono">/join/</span>
              <input
                type="text"
                value={settingsDraft.slug}
                onChange={(e) => setSettingsDraft((prev) => ({ ...prev, slug: e.target.value }))}
                className="w-full bg-transparent text-sm text-slate-900 font-mono focus:outline-none"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
              Allowed Corporate Email Domains
            </label>
            <p className="text-xs text-slate-500 mb-3">
              Only users with verified corporate emails under these domains will be permitted to join via the link.
            </p>

            {/* Domain Chips */}
            <div className="flex flex-wrap gap-2 mb-3">
              {settingsDraft.allowed_domains.map((domain) => (
                <span
                  key={domain}
                  className="px-3 py-1 bg-slate-100 border border-slate-200 text-indigo-700 rounded-lg text-xs font-mono flex items-center gap-2"
                >
                  @{domain}
                  <button
                    type="button"
                    onClick={() => handleRemoveDomain(domain)}
                    className="text-slate-400 hover:text-rose-600"
                    title={`Remove @${domain}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                placeholder="e.g. company.com or sub.company.com"
                value={settingsDraft.newDomainInput}
                onChange={(e) => setSettingsDraft((prev) => ({ ...prev, newDomainInput: e.target.value }))}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-indigo-500 shadow-xs"
              />
              <button
                type="button"
                onClick={handleAddDomain}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-xl flex items-center gap-1 shadow-xs transition shrink-0"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Domain
              </button>
            </div>
          </div>

          {/* Department Management */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
              Organization Departments
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {settingsDraft.departments.map((dept) => (
                <span
                  key={dept}
                  className="px-3 py-1 bg-slate-100 border border-slate-200 text-slate-800 rounded-lg text-xs flex items-center gap-2 font-medium"
                >
                  {dept}
                  <button
                    type="button"
                    onClick={() => handleRemoveDept(dept)}
                    className="text-slate-400 hover:text-rose-600"
                    title={`Remove ${dept}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                placeholder="e.g. Data Science, Marketing, Security..."
                value={settingsDraft.newDeptInput}
                onChange={(e) => setSettingsDraft((prev) => ({ ...prev, newDeptInput: e.target.value }))}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-indigo-500 shadow-xs"
              />
              <button
                type="button"
                onClick={handleAddDept}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-xl flex items-center gap-1 shadow-xs transition shrink-0"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Department
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <input
              type="checkbox"
              id="join_link_enabled"
              checked={settingsDraft.join_link_enabled}
              onChange={(e) => setSettingsDraft((prev) => ({ ...prev, join_link_enabled: e.target.checked }))}
              className="w-4 h-4 rounded text-indigo-600 bg-white border-slate-300 focus:ring-indigo-500"
            />
            <label htmlFor="join_link_enabled" className="text-xs text-slate-700 font-semibold cursor-pointer">
              Enable B2B Onboarding Join Link
            </label>
          </div>

          <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
            <button
              type="button"
              onClick={handleRegenerateLink}
              disabled={regeneratingToken}
              className="px-4 py-2.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-xl text-xs font-semibold flex items-center gap-2 transition disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${regeneratingToken ? "animate-spin" : ""}`} />
              Regenerate Private Token
            </button>

            <button
              type="submit"
              disabled={savingSettings}
              className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-xs transition disabled:opacity-50"
            >
              {savingSettings ? "Saving Changes..." : "Save Settings"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
